import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Popover} from "react-tiny-popover";
import PropTypes from "prop-types";
import log from "../../../util/log";
import popoverActions from "../../../actions/popover-actions";

class VFPopover extends Component {

  //This should be the same amount of time as the .fade-{in/out}-{left/top/right/down}
  //classes.  Otherwise you could get weird flickering and jitter.
  static ANIMATION_TIME_MS = 150;

  static getPopoverKey(popoverName, key){
    return `${popoverName}${key ? '-' + key : ''}`
  }

  constructor(props) {
    super(props);

    this.onClickOutSide = this.onClickOutSide.bind(this);
    this.getContent = this.getContent.bind(this);

    this.state = {
      popoverShouldBeOpen : false,
      isPopoverClosing : false,

      //react-tiny-popover can sometimes throw React error #185.  this is apparently a bug in their library
      //and it's not likely to get fixed.  It's also quite rare.
      //My fix here to catch this problem with an error boundary and hide the popover gracefully.
      //Since otherwise this is a crash, pretty much any attempt at graceful handling is preferable.
      //so we log it out, catch the crash, hide the popover and cleanup so that it will work the next time you show (hopefully).
      hasError : false,
    }
  }

  static getDerivedStateFromError() {
    return {hasError: true};
  }

  componentDidCatch(error, info) {
    log.error('VFPopover crashed while positioning; dropping the overlay and closing the popover.', error, info);

    //we caught an error.  lets close the popover here so that users can just re-open.
    //it's too late to recover anyway, we've unmounted, so this just heals our state.
    setTimeout(() => {
      if(this.props.hidePopover){
        this.props.hidePopover();
      }
    }, 0);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if(this.state.hasError){
      //reset our vars if closed due to error.
      if(!this.props.isPopoverOpen){
        this.setState({
          hasError : false,
          popoverShouldBeOpen : false,
          isPopoverClosing : false,
        })
      }
      return;
    }

    if(prevProps.isPopoverOpen && !this.props.isPopoverOpen){
      this.setState({
        isPopoverClosing : true,
      })
      setTimeout(() => {
        this.setState({
          isPopoverClosing : false,
          popoverShouldBeOpen : false
        })
      }, VFPopover.ANIMATION_TIME_MS) //same timeout as fade animation
    }
    else if(!prevProps.isPopoverOpen && this.props.isPopoverOpen){
      //opening, just do it.
      this.setState({
        popoverShouldBeOpen : true
      })
    }
  }

  onClickOutSide(){
    if(this.props.onClickOutside){
      this.props.onClickOutside(...arguments);
    }
  }

  getFadeClassForPosition(position){
    let {
      isPopoverClosing
    } = this.state;

    //log.log('closing popover class', position, isPopoverClosing);
    if(isPopoverClosing){
      if(position === "left"){
        return "fade-out-right";
      }
      else if(position === "right"){
        return "fade-out-left";
      }
      else if(position === "bottom"){
        return "fade-out-up";
      }
      else if(position === "top"){
        return "fade-out-down";
      }
    }
    else{
      if(position === "left"){
        return "fade-in-left";
      }
      else if(position === "right"){
        return "fade-in-right";
      }
      else if(position === "bottom"){
        return "fade-in-down";
      }
      else if(position === "top"){
        return "fade-in-up";
      }
    }
  }

  getContent(){
    if(this.props.getMenuContent){
      let {
        position
      } = arguments[0];
      //log.log('get popover content', arguments);
      return (
        <div className={this.getFadeClassForPosition(position)}>
          {this.props.getMenuContent(...arguments)}
        </div>
      )
    }
  }

  render() {
    let {
      children,
      positions,
      align,
      containerClassName,
      containerStyle,
      reposition
    } = this.props;

    let {
      popoverShouldBeOpen,
      hasError
    } = this.state;

    //might look weird...this is the TRIGGER of the popover, not the content.
    //so if an error exists, we want to render the trigger without wrapping Popover until the error is cleared
    //the users should not notice anything because the trigger will just exist.
    if(hasError){
      return children;
    }

    return (
      <Popover isOpen={popoverShouldBeOpen}
               onClickOutside={this.onClickOutSide}
               positions={positions}
               align={align}
               containerStyle={containerStyle}
               boundaryInset={15}
               containerClassName={containerClassName}
               reposition={reposition}
               content={this.getContent}>
        {children}
      </Popover>
    )
  }
}

VFPopover.propTypes = {
  getMenuContent: PropTypes.func.isRequired,
  reposition: PropTypes.bool,
  onClickOutside: PropTypes.func,
  isPopoverOpen: PropTypes.bool.isRequired,
  positions: PropTypes.array,
  align: PropTypes.string,
  containerClassName: PropTypes.string,
  containerStyle: PropTypes.object
}

const mapStateToProps = (state) => {
  return {}
};

const mapDispatchToProps = (dispatch) => {
  return {
    hidePopover: () => dispatch(popoverActions.hidePopover())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(VFPopover);